import { ListLivestockInsurancePriceDatesResponse, UnitOfMeasure } from '../../../../../Generated/Raven-Demeter';
import informationSvg from '../../../../Assets/Icons/info.svg';
import PageAccordion from '../../../../Components/Form/Buttons/Accordions/PageAccordion';
import LinkButton, { LinkButtonType } from '../../../../Components/Form/Buttons/LinkButton';
import NumberInput from '../../../../Components/Form/Inputs/NumberInput';
import RegexValidators from '../../../../Core/Validation/RegexValidators';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import { LiquidityRiskCalculationInputs } from '../../RiskDefinitions';
import GeneralRiskDateDropdown from '../CommonFields/GeneralRiskDateDropdown';
import GeneralRiskInsuranceStateDropdown from '../CommonFields/GeneralRiskInsuranceStateDropdown';
import GeneralRiskInsuranceYearDropdown from '../CommonFields/GeneralRiskInsuranceYearDropdown';
import { GeneralRiskCalculationInputs } from '../DairyRevenueProtection/DairyRevenueProtectionDefinitions';
import styles from '../InsuranceQuoteEstimator.module.scss';
import LiquidityRiskCommodityDropdown from './LivestockRevenueProtectionFields/LivestockRevenueProtectionCommodityDropdown';
import LiquidityRiskEndorsementLengthDropdown from './LivestockRevenueProtectionFields/LivestockRevenueProtectionEndorsementLengthDropdown';
import LiquidityRiskTypeDropdown from './LivestockRevenueProtectionFields/LivestockRevenueProtectionTypeDropdown';

interface ILivestockRevenueProtectionFormProps {
    tableCalculations: boolean;
    setTableCalculations: (tableCalculations: boolean) => void;
    calculationInputs: LiquidityRiskCalculationInputs | undefined;
    setCalculatorInputs: (value: LiquidityRiskCalculationInputs) => void;
    refreshListLivestockInsurancePrices: () => void;
    listLivestockInsurancePriceDatesResponse: ListLivestockInsurancePriceDatesResponse;
}

const LivestockRevenueProtectionForm: React.FC<ILivestockRevenueProtectionFormProps> = (props: ILivestockRevenueProtectionFormProps) => {
    // Application hooks.
    const [translations] = useLanguage();

    const handleRunCalculator = () => {
        props.refreshListLivestockInsurancePrices();
        props.setTableCalculations(!props.tableCalculations);
    };

    return (
        <div className={styles.insurances_quote_estimator}>
            <div className={styles.liquidity_risk_section}>
                <PageAccordion
                    title={translations.risk.insuranceQuoteEstimator.headers.livestockRevenueProtectionForm}
                    open
                    component={
                        <>
                            <div className={styles.liquidity_risk_section}>
                                <div className={styles.liquidity_risk_row}>
                                    <div className={styles.liquidity_risk_blue_box}>
                                        <img src={informationSvg} alt="#" /> {translations.risk.insuranceQuoteEstimator.text.fillOutTheForm}{' '}
                                    </div>
                                </div>
                            </div>
                            <div className={styles.liquidity_risk_section}>
                                <div className={styles.liquidity_risk_row}>
                                    <GeneralRiskInsuranceYearDropdown
                                        calculationInputs={props.calculationInputs!}
                                        setCalculatorInputs={props.setCalculatorInputs as (value: GeneralRiskCalculationInputs) => void}
                                        listLivestockInsurancePriceDatesResponse={props.listLivestockInsurancePriceDatesResponse!}
                                    />
                                    <GeneralRiskDateDropdown
                                        calculationInputs={props.calculationInputs!}
                                        setCalculatorInputs={props.setCalculatorInputs as (value: GeneralRiskCalculationInputs) => void}
                                        listLivestockInsurancePriceDatesResponse={props.listLivestockInsurancePriceDatesResponse!}
                                    />
                                    <GeneralRiskInsuranceStateDropdown
                                        calculationInputs={props.calculationInputs!}
                                        setCalculatorInputs={props.setCalculatorInputs as (value: GeneralRiskCalculationInputs) => void}
                                    />
                                </div>
                            </div>
                            <div className={styles.liquidity_risk_section}>
                                <div className={styles.liquidity_risk_row}>
                                    <LiquidityRiskCommodityDropdown
                                        calculationInputs={props.calculationInputs!}
                                        setCalculatorInputs={props.setCalculatorInputs}
                                    />
                                    <LiquidityRiskTypeDropdown calculationInputs={props.calculationInputs!} setCalculatorInputs={props.setCalculatorInputs} />
                                    <div className={styles.liquidity_risk_section}>
                                        <NumberInput
                                            title={translations.risk.margin.fields.animalHeadCount}
                                            required
                                            value={props.calculationInputs?.animalHeadCount}
                                            validation={RegexValidators.PositiveNumber}
                                            handleTextChange={(value) => props.setCalculatorInputs({ ...props.calculationInputs!, animalHeadCount: +value })}
                                            errorMessage={translations.marketIndicatorsManagement.messages.positiveNumber}
                                            track={{
                                                eventCategory: 'InsuranceQuoteEstimator',
                                                eventAction: 'LivestockAnimalHeadCountChanged',
                                                eventValue: props.calculationInputs?.animalHeadCount,
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className={styles.liquidity_risk_section}>
                                <div className={styles.liquidity_risk_row}>
                                    <div className={styles.liquidity_risk_section}>
                                        <NumberInput
                                            title={`${translations.risk.margin.fields.endWeight} (${
                                                translations.unitOfMeasure[`Short${UnitOfMeasure.HundredWeight}`]
                                            })`}
                                            required
                                            value={props.calculationInputs?.endWeight}
                                            validation={RegexValidators.PositiveNumber}
                                            handleTextChange={(value) => props.setCalculatorInputs({ ...props.calculationInputs!, endWeight: +value })}
                                            errorMessage={translations.marketIndicatorsManagement.messages.positiveNumber}
                                            track={{
                                                eventCategory: 'InsuranceQuoteEstimator',
                                                eventAction: 'LivestockEndWeightChanged',
                                                eventValue: props.calculationInputs?.endWeight,
                                            }}
                                        />
                                    </div>
                                    <div className={styles.liquidity_risk_section}>
                                        <NumberInput
                                            title={translations.risk.margin.fields.insuredSharedPercentage}
                                            required
                                            value={props.calculationInputs?.insuredSharedPercentage}
                                            validation={RegexValidators.PositiveNumberZeroThroughOneHundred}
                                            handleTextChange={(value) =>
                                                props.setCalculatorInputs({ ...props.calculationInputs!, insuredSharedPercentage: +value })
                                            }
                                            errorMessage={translations.marketIndicatorsManagement.messages.positiveNumberZeroThroughOneHundred}
                                            track={{
                                                eventCategory: 'InsuranceQuoteEstimator',
                                                eventAction: 'LivestockInsuredSharedPercentageChanged',
                                                eventValue: props.calculationInputs?.insuredSharedPercentage,
                                            }}
                                        />
                                    </div>
                                    <LiquidityRiskEndorsementLengthDropdown
                                        calculationInputs={props.calculationInputs!}
                                        setCalculatorInputs={props.setCalculatorInputs}
                                    />
                                </div>
                            </div>
                            <div className={styles.liquidity_risk_section}>
                                <LinkButton
                                    disabled={
                                        !props.calculationInputs?.animalHeadCount ||
                                        props.calculationInputs?.type.length === 0 ||
                                        props.calculationInputs?.endorsementLength.length === 0 ||
                                        !props.calculationInputs.endWeight ||
                                        props.calculationInputs.insuredSharedPercentage < 0 ||
                                        props.calculationInputs.insuredSharedPercentage > 100 ||
                                        (!props.calculationInputs.insuredSharedPercentage && props.calculationInputs.insuredSharedPercentage !== 0)
                                    }
                                    title={translations.words.calculate}
                                    type={LinkButtonType.Blue}
                                    onClick={handleRunCalculator}
                                    track={{ eventCategory: 'InsuranceQuoteEstimator', eventAction: 'CalculateClicked' }}
                                />
                            </div>
                        </>
                    }
                />
            </div>
        </div>
    );
};

export default LivestockRevenueProtectionForm;
